import { ColumnsType } from "antd/es/table";
import { COLOR_TYPE, PropertyData, UserData } from "./CampaignReport.type";
import { Tag } from "Components";
import { PDF_URL } from "PhishingManager/service/index.constant";
import { capitalize } from "PhishingManager/utils";
import { TFunction } from "i18next";

export const STATUS_COLOR: Record<string, COLOR_TYPE> = {
    high: { color: '#006CA3', bg: '#E6F7FD' },
    medium: { bg: "#FFF7E0", color: "#000000" },
    low: { bg: "#FBEAEA", color: "#e03a3e" },
};

export const RESULT_COLOR: Record<string, COLOR_TYPE> = {
    Passed: { color: '#006CA3', bg: '#E6F7FD' },
    Failed: { bg: "#FBEAEA", color: "#e03a3e" },
};

export const INTERACTION_COLOR: COLOR_TYPE = { color: '#636766', bg: '#EAEAEB' };

export const GET_COLOR_BY_SCORE = (score: number) => {
    const status = score >= 94 ? "high" : (score < 94 && score >= 90) ? "medium" : "low";
    const { bg, color } = STATUS_COLOR[status];
    return {
        bg, color,
        tag: score >= 97 ? "phishingManager.report.table.tags.excellent" : (score < 97 && score >= 94) ? "phishingManager.report.table.tags.good" : (score < 94 && score >= 90) ? "phishingManager.report.table.tags.fair" : "phishingManager.report.table.tags.poor"
    };
};

const renderStatusTag = (value: number | string, color: string, bg: string) => {
    return value ? <Tag className='status-tag' style={{ color, background: bg }}>{value}</Tag> : "-";
};

const renderPercentTag = (value: number) => {
    const { color, bg } = INTERACTION_COLOR;
    return <Tag className='status-tag' style={{ color, background: bg }}>{`${value}%`}</Tag>;
};

export const INDIVIDUAL_TABLE_COLUMNS = (t: TFunction) : ColumnsType<UserData> => [
    {
        title: t('phishingManager.report.table.individualTable.name'),
        key: "name",
        render: (_, record) => `${record.first_name || ""} ${record.last_name || ""}`.trim(),
    },
    {
        title: t('phishingManager.report.table.individualTable.email'),
        dataIndex: "email",
        key: "email",
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: t('phishingManager.report.table.individualTable.property'),
        dataIndex: "properties",
        key: "properties",
        render: (value) => value?.length > 1 ? value?.length : capitalize(value?.[0] || '-')
    },
    {
        title: t('phishingManager.report.table.individualTable.group'),
        dataIndex: "groups",
        key: "groups",
        render: (value) => value?.length > 1 ? value?.length : capitalize(value?.[0] || '-')
    },
    {
        title: t('phishingManager.report.table.individualTable.source'),
        dataIndex: "source",
        key: "source",
        render: (result, record) => {
            const value = record.email_delivery === "No" ? 0 : result;
            return value || "-";
        }
    },
    {
        title: t('phishingManager.report.table.individualTable.result'),
        dataIndex: "result",
        key: "result",
        align: "center",
        // Optional: Color code Passed vs Failed
        render: (result, record) => {
            const { color, bg } = RESULT_COLOR[result] || RESULT_COLOR.Failed;
            const value = record.email_delivery === "No" ? 0 : result;
            return renderStatusTag(value, color, bg);
        }
    },
    {
        title: t('phishingManager.report.table.individualTable.interaction'),
        dataIndex: "interaction",
        key: "interaction",
        align: "center",
        render: (interaction, record) => {
            const { color, bg } = INTERACTION_COLOR;
            const value = record.email_delivery === "No" ? 0 : interaction;
            return renderStatusTag(value, color, bg);
        }
    },
]

export const PROPERTY_TABLE_COLUMNS = (t: TFunction) : ColumnsType<PropertyData> => [
    {
        title:  t('phishingManager.report.table.propertyTable.name'),
        dataIndex: "group_name",
        key: "group_name",
    },
    {
        title: t('phishingManager.report.table.propertyTable.passRate'),
        dataIndex: "pass_rate",
        key: "pass_rate",
        align: "center",
        render: (value: number) => renderPercentTag(value),
    },
    {
        title:t('phishingManager.report.table.propertyTable.openRate'),
        dataIndex: "opened_rate",
        key: "opened_rate",
        align: "center",
        render: (value: number) => renderPercentTag(value),
    },
    {
        title: t('phishingManager.report.table.propertyTable.clickRate'),
        dataIndex: "clicked_rate",
        key: "clicked_rate",
        align: "center",
        render: (value: number) => renderPercentTag(value),
    },
    {
        title: t('phishingManager.report.table.propertyTable.submissionRate'),
        dataIndex: "submission_rate",
        key: "submission_rate",
        align: "center",
        render: (value: number) => renderPercentTag(value),
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: t('phishingManager.report.table.propertyTable.emailSent'),
        dataIndex: "email_sent_count",
        key: "email_sent_count",
    },
    {
        title: t('phishingManager.report.table.propertyTable.emailOpened'),
        dataIndex: "email_opened_count",
        key: "email_opened_count",
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: t('phishingManager.report.table.propertyTable.clickedLink'),
        dataIndex: "clicked_link_count",
        key: "clicked_link_count",
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: t('phishingManager.report.table.propertyTable.submittedData'),
        dataIndex: "submitted_data_count",
        key: "submitted_data_count",
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: t('phishingManager.report.table.propertyTable.totalUsers'),
        dataIndex: "users_count",
        key: "users_count",
    }
];

export const INTERACTION_PDF_URL =  `${PDF_URL}/learn-more-pages/interpreting_results.pdf`;
export const AUTOMATION_PDF_URL = `${PDF_URL}/learn-more-pages/automation_detection.pdf`;